import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Marketing.css';
import { toast } from '../components/feedback/Feedback';

/**
 * /workspace-select
 * Shows when a user belongs to multiple orgs.
 * Orgs are read from sessionStorage (set by AuthCallbackPage).
 */
const WorkspaceSelectorPage = () => {
    const navigate = useNavigate();
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selecting, setSelecting] = useState(null);

    useEffect(() => {
        const stored = sessionStorage.getItem('ra-pending-orgs');
        if (!stored) {
            navigate('/login', { replace: true });
            return;
        }
        setOrgs(JSON.parse(stored));
    }, [navigate]);

    const selectOrg = async (orgId) => {
        setSelecting(orgId);
        setLoading(true);

        const token = sessionStorage.getItem('ra-pending-token');
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }

        try {
            const response = await fetch('/api/auth/set-active-org', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ orgId }),
            });

            if (response.ok) {
                // Persist role for Header dropdown
                const selected = orgs.find(o => o.id === orgId);
                if (selected) {
                    localStorage.setItem('ra_org_role', selected.role);
                    localStorage.setItem('ra_active_org_id', selected.id);
                }
                // Clean up sessionStorage
                sessionStorage.removeItem('ra-pending-orgs');
                sessionStorage.removeItem('ra-pending-token');
                navigate('/dashboard', { replace: true });
            } else {
                toast.error('Failed to select workspace. Please try again.');
                setLoading(false);
                setSelecting(null);
            }
        } catch (err) {
            console.error('[WORKSPACE_SELECT] Error:', err);
            toast.error('Something went wrong. Please try again.');
            setLoading(false);
            setSelecting(null);
        }
    };

    const roleLabel = (role) => {
        const labels = { owner: '👑 Owner', manager: '🔧 Manager', sales: '📊 Sales', assistant: '📋 Assistant' };
        return labels[role] || role;
    };

    return (
        <div className="login-container">
            <div className="login-card" style={{ maxWidth: '480px' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏢</div>
                    <h2 className="login-title">Select Workspace</h2>
                    <p style={{ color: '#8892B0', fontSize: '0.9rem' }}>
                        You have access to multiple workspaces. Choose one to continue.
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {orgs.map((org) => (
                        <button
                            key={org.id}
                            onClick={() => selectOrg(org.id)}
                            disabled={loading}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '1rem 1.25rem',
                                background: selecting === org.id ? 'rgba(100,255,218,0.12)' : 'rgba(14,36,64,0.6)',
                                border: `1px solid ${selecting === org.id ? '#64FFDA' : '#1E3A5F'}`,
                                borderRadius: '10px',
                                color: '#CDD6F4',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                textAlign: 'left',
                                transition: 'all 0.2s',
                                opacity: loading && selecting !== org.id ? 0.5 : 1,
                            }}
                            onMouseEnter={e => !loading && (e.currentTarget.style.borderColor = '#64FFDA88')}
                            onMouseLeave={e => !loading && selecting !== org.id && (e.currentTarget.style.borderColor = '#1E3A5F')}
                        >
                            <div>
                                <div style={{ fontWeight: '600', marginBottom: '0.2rem' }}>{org.name}</div>
                                <div style={{ fontSize: '0.8rem', color: '#8892B0' }}>{roleLabel(org.role)}</div>
                            </div>
                            <div style={{ color: '#64FFDA', fontSize: '1.2rem' }}>
                                {selecting === org.id ? '⏳' : '→'}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WorkspaceSelectorPage;
